import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lightbulb, BookOpen, Home, Trees, Wrench, ArrowRight, ArrowLeft, Calendar, Share2, Link2, Check, Facebook, MessageCircle, Clock, Gauge } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { SEOHead, organizationSchema, toIsoDuration } from '../components/SEOHead';
import { ConseilMedia } from '../components/ConseilMedia';
import { toast } from 'sonner';
import axios from 'axios';
import { VIEWPORT_ONCE } from '../lib/motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SITE_URL = 'https://ptitservices.com';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png';

const CAT_LABELS = { menage: 'Ménage & Nettoyage', jardinage: 'Jardinage', bricolage: 'Bricolage' };
const CAT_ICONS = { menage: Home, jardinage: Trees, bricolage: Wrench };
const TYPE_LABELS = { astuce: 'Astuce', tuto: 'Tutoriel' };
const TYPE_ICONS = { astuce: Lightbulb, tuto: BookOpen };

export function ShareButtons({ url, title, compact = false }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url.startsWith('http') ? url : `${SITE_URL}${url}`;
  const text = encodeURIComponent(`${title} — Ptitconseils par Ptitservices.com`);
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const wa = `https://wa.me/?text=${text}%20${encodeURIComponent(shareUrl)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Lien copié');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier le lien');
    }
  };

  const size = compact ? 'h-8 w-8' : 'h-9 px-3';
  return (
    <div className="flex items-center gap-2" data-testid="share-buttons">
      {!compact && <span className="text-xs text-muted-foreground flex items-center gap-1 mr-1"><Share2 className="h-3.5 w-3.5" /> Partager</span>}
      <a href={fb} target="_blank" rel="noopener noreferrer" aria-label="Partager sur Facebook" data-testid="share-facebook"
         className={`${size} inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-card text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors`}>
        <Facebook className="h-4 w-4" />{!compact && 'Facebook'}
      </a>
      <a href={wa} target="_blank" rel="noopener noreferrer" aria-label="Partager sur WhatsApp" data-testid="share-whatsapp"
         className={`${size} inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-card text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors`}>
        <MessageCircle className="h-4 w-4" />{!compact && 'WhatsApp'}
      </a>
      <button type="button" onClick={copy} aria-label="Copier le lien" data-testid="share-copy"
        className={`${size} inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-card text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors`}>
        {copied ? <Check className="h-4 w-4 text-[hsl(var(--primary))]" /> : <Link2 className="h-4 w-4" />}{!compact && (copied ? 'Copié' : 'Copier le lien')}
      </button>
    </div>
  );
}

export default function ConseilPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    axios.get(`${BACKEND_URL}/api/ptitconseils/${slug}`)
      .then((res) => {
        setItem(res.data.item);
        setRelated(res.data.related || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 space-y-4" data-testid="conseil-loading">
        <Skeleton className="h-6 w-40" /><Skeleton className="h-10 w-3/4" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" /><Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (notFound || !item) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="conseil-not-found">
        <h1 className="font-['Space_Grotesk'] text-2xl font-semibold mb-3">Conseil introuvable</h1>
        <p className="text-muted-foreground mb-6">Ce conseil n'existe plus ou l'adresse est incorrecte.</p>
        <Link to="/ptitconseils"><Button><ArrowLeft className="mr-2 h-4 w-4" />Retour aux Ptitconseils</Button></Link>
      </div>
    );
  }

  const CatIcon = CAT_ICONS[item.category] || Home;
  const TypeIcon = TYPE_ICONS[item.type] || Lightbulb;
  const path = `/ptitconseils/${item.slug}`;
  const paragraphs = (item.contenu || '').split('\n\n').filter(Boolean).map((p) => item.type === 'tuto' ? p.replace(/^\s*(?:étape\s*)?\d+\s*[.):-]\s*/i, '') : p);
  const dateLabel = item.date ? new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const schema = {
    "@context": "https://schema.org",
    "@type": item.type === 'tuto' ? 'HowTo' : 'Article',
    "headline": item.titre,
    "name": item.titre,
    "description": item.resume || paragraphs[0] || '',
    "datePublished": item.date,
    "inLanguage": "fr-FR",
    "keywords": (item.tags || []).join(', '),
    "dateModified": item.date,
    "author": organizationSchema(),
    "publisher": organizationSchema(),
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
    "image": item.image_url ? `${SITE_URL}${item.image_url}` : LOGO_URL,
    ...(item.video_url && item.image_url && item.date ? { "video": { "@type": "VideoObject", "name": item.titre, "description": item.resume || paragraphs[0] || item.titre, "thumbnailUrl": [`${SITE_URL}${item.image_url}`], "contentUrl": `${SITE_URL}${item.video_url}`, "uploadDate": item.date, "duration": "PT5S" } } : {}),
    ...(item.type === 'tuto'
      ? {
          "step": paragraphs.map((p, i) => {
            const m = p.match(/^\*\*(.+?)\*\*\s*:?\s*(.*)$/s);
            const text = (m ? m[2] : p).replace(/\*\*/g, '').trim() || p.replace(/\*\*/g, '').trim();
            return { "@type": "HowToStep", "position": i + 1, "name": m ? m[1].trim() : `Étape ${i + 1}`, "text": text, "url": `${SITE_URL}${path}#etape-${i + 1}` };
          }),
          ...(toIsoDuration(item.duree) ? { "totalTime": toIsoDuration(item.duree) } : {}),
          ...(item.materiel?.length ? { "tool": item.materiel.map((m) => ({ "@type": "HowToTool", "name": m })) } : {}),
        }
      : { "articleBody": item.contenu, "articleSection": CAT_LABELS[item.category] || 'Conseils' }),
  };

  return (
    <div data-testid="conseil-page">
      <SEOHead
        title={item.titre}
        description={item.resume || paragraphs[0]?.slice(0, 155) || ''}
        path={path}
        type="article"
        schema={schema}
      />

      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-foreground">Accueil</Link><span>/</span>
            <Link to="/ptitconseils" className="hover:text-foreground">Ptitconseils</Link><span>/</span>
            <span className="text-foreground font-medium truncate max-w-[200px] sm:max-w-none">{item.titre}</span>
          </nav>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]"><TypeIcon className="h-3.5 w-3.5 mr-1" />{TYPE_LABELS[item.type] || 'Conseil'}</Badge>
              <Badge variant="secondary"><CatIcon className="h-3.5 w-3.5 mr-1" />{CAT_LABELS[item.category] || item.category}</Badge>
              {dateLabel && <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" />{dateLabel}</span>}
            </div>
            <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight leading-tight mb-4" data-testid="conseil-title">{item.titre}</h1>
            {item.resume && <p className="text-lg text-muted-foreground leading-relaxed mb-6">{item.resume}</p>}
            <ShareButtons url={path} title={item.titre} />
          </motion.div>
        </div>
      </section>

      <section className="py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {(item.image_url || item.video_url) && (
            <motion.div className="rounded-2xl overflow-hidden shadow-xl mb-8 -mt-16 sm:-mt-20 relative z-10" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} data-testid="conseil-hero-image">
              <ConseilMedia item={item} className="w-full h-56 sm:h-80" loading="eager" testId="conseil-hero-media" showBadge />
            </motion.div>
          )}

          {item.type === 'tuto' && (item.duree || item.difficulte || item.materiel?.length > 0) && (
            <Card className="p-5 sm:p-6 mb-6 border-2 border-[hsl(var(--primary))]/15 bg-[hsl(var(--brand-mint))]/20" data-testid="conseil-tuto-meta">
              <div className="flex flex-wrap gap-3 mb-3">
                {item.duree && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-border text-sm"><Clock className="h-4 w-4 text-[hsl(var(--primary))]" /> {item.duree}</span>}
                {item.difficulte && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-border text-sm"><Gauge className="h-4 w-4 text-[hsl(var(--primary))]" /> Niveau {item.difficulte}</span>}
              </div>
              {item.materiel?.length > 0 && (
                <>
                  <h2 className="font-['Space_Grotesk'] font-semibold text-sm mb-2 flex items-center gap-2"><Wrench className="h-4 w-4 text-[hsl(var(--primary))]" /> Matériel nécessaire</h2>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {item.materiel.map((m) => <li key={m} className="text-sm text-muted-foreground flex items-start gap-2"><Check className="h-4 w-4 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />{m}</li>)}
                  </ul>
                </>
              )}
            </Card>
          )}

          <Card className="p-6 sm:p-8">
            <div className="space-y-5" data-testid="conseil-content">
              {paragraphs.map((p, i) => (
                <motion.div key={`${i}-${String(p).slice(0, 24)}`} id={item.type === 'tuto' ? `etape-${i + 1}` : undefined} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEWPORT_ONCE} transition={{ delay: 0.05 * Math.min(i, 6) }} className={item.type === 'tuto' ? 'flex gap-4' : ''}>
                  {item.type === 'tuto' && (
                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</div>
                  )}
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{p}</p>
                </motion.div>
              ))}
            </div>

            {(item.pourquoi || item.erreur || item.astuce_pro) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8" data-testid="conseil-extras">
                {item.pourquoi && (
                  <div className="p-4 rounded-xl bg-[hsl(var(--brand-mint))]/30 border border-[hsl(var(--primary))]/15">
                    <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-1">Pourquoi ça marche</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.pourquoi}</p>
                  </div>
                )}
                {item.erreur && (
                  <div className="p-4 rounded-xl bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/20">
                    <p className="text-xs font-semibold text-[hsl(var(--accent))] mb-1">L'erreur à éviter</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.erreur}</p>
                  </div>
                )}
                {item.astuce_pro && (
                  <div className="p-4 rounded-xl bg-[hsl(var(--brand-mint))]/30 border border-[hsl(var(--primary))]/15 sm:col-span-2">
                    <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-1">L'astuce de l'Easer</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.astuce_pro}</p>
                  </div>
                )}
              </div>
            )}
            {item.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8 pt-5 border-t border-border">
                {item.tags.map((t) => <span key={t} className="text-xs px-3 py-1 rounded-full bg-[hsl(var(--secondary))] text-muted-foreground">#{t}</span>)}
              </div>
            )}
            <div className="mt-8 pt-5 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <ShareButtons url={path} title={item.titre} />
              <Link to="/devis">
                <Button className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="conseil-quote-button">Confiez-nous cette tâche <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </Link>
            </div>
          </Card>

          <div className="mt-8 p-5 rounded-xl bg-[hsl(var(--brand-mint))]/40 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <h2 className="font-['Space_Grotesk'] font-semibold mb-1">Pas le temps de le faire vous-même ?</h2>
              <p className="text-sm text-muted-foreground">Un Easer dédié s'en occupe. Ménage dès 14,99 €/h et jardinage dès 24,95 €/h après crédit d'impôt 50% en avance immédiate.</p>
            </div>
            <Link to="/services"><Button variant="outline" size="sm">Voir nos services</Button></Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-10 sm:py-14 bg-[hsl(var(--secondary))]/60">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-6">Dans la même catégorie</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="conseil-related">
              {related.map((r) => {
                const RIcon = TYPE_ICONS[r.type] || Lightbulb;
                return (
                  <Link key={r.slug} to={`/ptitconseils/${r.slug}`}>
                    <Card className="h-full hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] group overflow-hidden">
                      {r.image_url && <img src={r.image_url} alt="" loading="lazy" className="w-full h-32 object-cover" />}
                      <div className="p-5">
                      <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground"><RIcon className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />{TYPE_LABELS[r.type]}</div>
                      <h3 className="font-semibold text-sm group-hover:text-[hsl(var(--primary))] transition-colors line-clamp-2">{r.titre}</h3>
                      {r.resume && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{r.resume}</p>}
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-8">
              <Link to="/ptitconseils"><Button variant="outline"><ArrowLeft className="mr-2 h-4 w-4" />Tous les Ptitconseils</Button></Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
